import { AuthBindings } from '@refinedev/core';
import { authService } from '../services/auth.service';
import { LoginRequest } from '../types/auth.types';
import { MAIN_AUTH_CONFIG } from '../config/auth';

/**
 * Refine 프레임워크용 메인 인증 프로바이더
 * 새로운 인증 시스템과 통합
 */
export const mainAuthProvider: AuthBindings = {
  login: async ({ username, password, rememberMe = false }) => {
    try {
      console.log('🔍 [mainAuthProvider.login] 로그인 시도:', { username, rememberMe });

      const credentials: LoginRequest = {
        username,
        password,
        rememberMe,
      };

      const loginResponse = await authService.login(credentials);

      console.log('✅ [mainAuthProvider.login] 로그인 성공:', loginResponse);
      console.log('🔍 [mainAuthProvider.login] 저장된 토큰 확인:', authService.getStoredTokens());
      console.log(
        '🔍 [mainAuthProvider.login] requireTermsAgreement:',
        loginResponse.requireTermsAgreement
      );
      console.log(
        '🔍 [mainAuthProvider.login] requirePasswordChange:',
        loginResponse.requirePasswordChange
      );

      // 파트너 사용자는 배송현황 페이지로 리다이렉트
      const storedUser = authService.getStoredUser();
      const isPartner = storedUser?.userType === 'PARTNER';
      const defaultPath = isPartner ? '/status' : '/dashboard';

      // 약관 동의와 비밀번호 변경 모두 필요한 경우 (순차 처리)
      if (
        loginResponse.requireTermsAgreement === true &&
        loginResponse.requirePasswordChange === true
      ) {
        console.log('⚠️ [mainAuthProvider] 약관 동의 + 비밀번호 변경 필요 (순차 처리)');
        sessionStorage.setItem('show_terms_agreement', 'true');
        // 약관 동의 완료 후 비밀번호 변경 알림을 표시하기 위한 플래그
        sessionStorage.setItem('password_change_after_terms', 'true');
        return {
          success: true,
          redirectTo: defaultPath,
        };
      }
      // 약관 동의만 필요한 경우
      else if (loginResponse.requireTermsAgreement === true) {
        console.log('⚠️ [mainAuthProvider] 약관 동의 필요');
        sessionStorage.setItem('show_terms_agreement', 'true');
        return {
          success: true,
          redirectTo: defaultPath,
        };
      }
      // 비밀번호 변경만 필요한 경우 (임시 비밀번호)
      else if (loginResponse.requirePasswordChange === true) {
        console.log('🔒 [mainAuthProvider] 비밀번호 변경 필요 - Settings 페이지로 직접 이동');
        return {
          success: true,
          redirectTo: '/settings?openPasswordDialog=true',
        };
      }
      // 비밀번호 만료된 경우 (90일 경과)
      else if (loginResponse.passwordExpired === true) {
        console.log('⏰ [mainAuthProvider] 비밀번호 만료 감지 - /settings로 리다이렉트');
        localStorage.setItem('dream_show_password_expiry_notification', 'true');
        window.dispatchEvent(new Event('dream-password-expiry-notification'));
        return {
          success: true,
          redirectTo: '/settings',
        };
      }

      return {
        success: true,
        redirectTo: defaultPath,
      };
    } catch (error) {
      console.error('❌ [mainAuthProvider.login] 로그인 실패:', error);
      const errorMessage = error instanceof Error ? error.message : '로그인에 실패했습니다.';
      return {
        success: false,
        error: {
          name: 'LoginError',
          message: errorMessage,
        },
      };
    }
  },

  logout: async () => {
    try {
      await authService.logout();
      return {
        success: true,
        redirectTo: '/login',
      };
    } catch (error) {
      // 로그아웃 실패해도 로그인 페이지로 이동
      return {
        success: true,
        redirectTo: '/login',
      };
    }
  },

  check: async () => {
    // Check for tokens in both new and legacy storage keys
    const { accessToken } = authService.getStoredTokens();

    // Fallback to legacy auth_token for backward compatibility
    const legacyToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

    const token = accessToken || legacyToken;

    console.log('🔍 [mainAuthProvider.check] accessToken:', accessToken);
    console.log('🔍 [mainAuthProvider.check] legacyToken:', legacyToken);
    console.log('🔍 [mainAuthProvider.check] final token:', token);

    if (!token) {
      console.log('❌ [mainAuthProvider.check] 토큰 없음 - 로그인 페이지로 리다이렉트');
      return {
        authenticated: false,
        redirectTo: '/login',
      };
    }

    // Validate token by decoding JWT and checking expiration
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      console.log('🔍 [mainAuthProvider.check] JWT payload:', payload);
      console.log('🔍 [mainAuthProvider.check] 현재 시간:', currentTime);
      console.log('🔍 [mainAuthProvider.check] 토큰 만료 시간:', payload.exp);

      // Check if token is expired
      if (payload.exp && payload.exp < currentTime) {
        console.log('❌ [mainAuthProvider.check] 토큰 만료됨 - 로그인 페이지로 리다이렉트');
        // Token expired - clear storage
        localStorage.removeItem(MAIN_AUTH_CONFIG.tokenKey);
        sessionStorage.removeItem(MAIN_AUTH_CONFIG.tokenKey);
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');

        return {
          authenticated: false,
          redirectTo: '/login',
        };
      }

      console.log('✅ [mainAuthProvider.check] 인증 성공');
      return { authenticated: true };
    } catch (error) {
      console.error('❌ [mainAuthProvider.check] JWT 파싱 실패:', error);
      // Invalid token format - clear storage
      localStorage.removeItem(MAIN_AUTH_CONFIG.tokenKey);
      sessionStorage.removeItem(MAIN_AUTH_CONFIG.tokenKey);
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_token');

      return {
        authenticated: false,
        redirectTo: '/login',
      };
    }
  },

  getPermissions: async () => {
    const user = authService.getStoredUser();
    return user?.permissions || null;
  },

  getIdentity: async () => {
    const user = authService.getStoredUser();

    if (!user) {
      return null;
    }

    // JWT 토큰에서 userId 추출 (fallback)
    let userId = user.id;
    if (!userId || userId === 0) {
      try {
        const { accessToken } = authService.getStoredTokens();
        if (accessToken) {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          userId = payload.userId || user.id;
          console.log('🔍 [mainAuthProvider] JWT에서 추출한 userId:', userId);
        }
      } catch (error) {
        console.error('JWT 파싱 실패:', error);
      }
    }

    return {
      id: String(userId), // 숫자를 문자열로 변환
      name: user.fullName,
      email: user.email,
      fullName: user.fullName,
      username: user.username, // 관리자 체크용
      avatar: undefined,
    };
  },

  onError: async error => {
    if (error?.response?.status === 401) {
      return {
        logout: true,
        redirectTo: '/login',
        error,
      };
    }
    return { error };
  },
};
